'use client';

import { c as _c } from "react/compiler-runtime";
import { type JSX, useRef, useState } from 'react';
import { AnimatedShowcaseCard } from '@/components/support-mainnet/markets-showcase/animated-showcase-card';
import { DebateBubble } from '@/components/support-mainnet/markets-showcase/debate-bubble';
import { CENTER_SHOWCASE_MARKET, LEFT_SHOWCASE_MARKET, RIGHT_SHOWCASE_MARKET } from '@/components/support-mainnet/markets-showcase/fixture';
import { StaticShowcaseCard } from '@/components/support-mainnet/markets-showcase/static-showcase-card';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { cn } from '@/lib/ui/utils';

/** Tailwind must see full class strings — do not build these with template literals. */
const LEFT_FLANK_RIGHT_CLASS = 'right-[calc(50%+120px)]' as const; /* centerHalf(204) − left overlap(84) */
const RIGHT_FLANK_LEFT_CLASS = 'left-[calc(50%+131px)]' as const; /* centerHalf(204) − right overlap(73) */

/** Previous flank scale 0.8, bumped 15% for legibility. */
export const FLANK_CARD_SCALE: number = 0.8 * 1.15;
export const SHOWCASE_FADE_WIDTH_PX = 120;

/** How far flank cards slide outward when the center card is hovered (book-open effect). */
export const SHOWCASE_FLANK_HOVER_SHIFT_PX = 56;

/** Fraction of the stage visible before debate bubbles begin their reveal. */
export const SHOWCASE_DEBATE_REVEAL_THRESHOLD = 0.45;
export type ShowcaseStageTitles = {
  center: string;
  left: string;
  right: string;
  leftDebate: string;
  rightDebate: string;
};
function ShowcaseFlankFade(t0) {
  const $ = _c(5);
  const {
    side
  } = t0;
  const t1 = side === "left" ? "right-full bg-gradient-to-l from-background-secondary to-transparent" : "left-full bg-gradient-to-r from-background-secondary to-transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("pointer-events-none absolute top-0 z-[15] hidden h-full w-[120px] md:block", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = side === "left" ? "markets-showcase-fade-left" : "markets-showcase-fade-right";
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <div aria-hidden={true} className={t2} data-testid={t3} />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
const debateBubbleRevealClassName = 'transition-[opacity,transform] duration-slow ease-default motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none';
const debateBubbleHiddenClassName = 'opacity-0 translate-y-3';
const debateBubbleVisibleClassName = 'opacity-100 translate-y-0';

/** Net bubble tilt — single rotation so label text is not nested inside the card scale/rotate. */
const LEFT_DEBATE_ROTATION_CLASS = '-rotate-[21.7deg]' as const; /* ARBITRARY: Figma −8° card + −13.7° bubble */
const RIGHT_DEBATE_ROTATION_CLASS = 'rotate-[38deg]' as const; /* ARBITRARY: Figma 8° card + 30° bubble */

export function ShowcaseStage(t0) {
  const $ = _c(32);
  const {
    titles
  } = t0;
  const [debateRevealed, setDebateRevealed] = useState(false);
  const stageRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      threshold: SHOWCASE_DEBATE_REVEAL_THRESHOLD
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting: isStageInView
  } = useIntersectionObserver(stageRef, t1);
  if (isStageInView && !debateRevealed) {
    setDebateRevealed(true);
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ShowcaseFlankFade side="left" />;
    t3 = <ShowcaseFlankFade side="right" />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isStageInView || $[4] !== titles.center) {
    t4 = <div className="peer/center relative z-sticky mx-auto w-full max-w-[408px]" data-testid="markets-showcase-center">{t2}{t3}<AnimatedShowcaseCard market={CENTER_SHOWCASE_MARKET} simulationEnabled={isStageInView} title={titles.center} /></div>;
    $[3] = isStageInView;
    $[4] = titles.center;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("absolute top-1/2 z-[1] hidden h-[312px] -translate-y-1/2 md:flex md:items-start", LEFT_FLANK_RIGHT_CLASS, "transition-transform duration-fast ease-default motion-reduce:transition-none", "peer-hover/center:-translate-x-14");
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== titles.left) {
    t6 = <div className="origin-bottom-right scale-[0.92] -rotate-[8deg]"><StaticShowcaseCard market={LEFT_SHOWCASE_MARKET} testId="markets-showcase-left-card" title={titles.left} /></div>;
    $[7] = titles.left;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = debateRevealed ? debateBubbleVisibleClassName : debateBubbleHiddenClassName;
  let t8;
  if ($[9] !== t7) {
    t8 = cn("bottom-0 left-0 z-sticky -translate-x-[14%] translate-y-[28%]", debateBubbleRevealClassName, t7);
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8 || $[12] !== titles.leftDebate) {
    t9 = <DebateBubble bubbleClassName="bg-[var(--brand-300)]" className={t8} label={titles.leftDebate} rotationClassName={LEFT_DEBATE_ROTATION_CLASS} tail="brand-left" testId="markets-showcase-debate-left" />;
    $[11] = t8;
    $[12] = titles.leftDebate;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t9) {
    t10 = <div className={t5} data-testid="markets-showcase-flank-left"><div className="relative">{t6}{t9}</div></div>;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("absolute top-1/2 z-[1] hidden h-[312px] -translate-y-1/2 md:flex md:items-start", RIGHT_FLANK_LEFT_CLASS, "transition-transform duration-fast ease-default motion-reduce:transition-none", "peer-hover/center:translate-x-14");
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== titles.right) {
    t12 = <div className="origin-bottom-left scale-[0.92] rotate-[8deg]"><StaticShowcaseCard market={RIGHT_SHOWCASE_MARKET} testId="markets-showcase-right-card" title={titles.right} /></div>;
    $[18] = titles.right;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = debateRevealed ? debateBubbleVisibleClassName : debateBubbleHiddenClassName;
  let t14;
  if ($[20] !== t13) {
    t14 = cn("left-[172px] top-[34px] z-sticky -rotate-[8deg]", debateBubbleRevealClassName, "delay-75", t13);
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== titles.rightDebate) {
    t15 = <DebateBubble bubbleClassName="bg-outcome-cyan-text" className={t14} label={titles.rightDebate} rotationClassName={RIGHT_DEBATE_ROTATION_CLASS} tail="cyan-right" testId="markets-showcase-debate-right" />;
    $[22] = t14;
    $[23] = titles.rightDebate;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t15) {
    t16 = <div className={t11} data-testid="markets-showcase-flank-right"><div className="relative">{t12}{t15}</div></div>;
    $[25] = t12;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t10 || $[29] !== t16 || $[30] !== t4) {
    t17 = <div ref={stageRef} className="relative mx-auto min-h-[18rem] w-full max-w-[920px] md:min-h-[20rem]" data-testid="markets-showcase-stage">{t4}{t10}{t16}</div>;
    $[28] = t10;
    $[29] = t16;
    $[30] = t4;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  return t17;
}